import { type EmbedTargetType } from './embed-target'
import { VideoJSMediaControls } from '../media/VideoJSMediaControls'
import { type MediaControls } from '../media'
import { type DetectEmbedTargetsOptions, type EmbedTargetFactory } from './embed-target-factory'
import { BaseEmbedTarget } from './base'
import { type VideoJsPlayer } from 'video.js'
import type Component from 'video.js'
import { waitUntil } from '../../utils/wait-until'
import { type EmbedBeforeCreateElementContext, type EmbedElementContext } from '../element-embedder'
import { type IFrameEmbedHandler } from '../embed-strategies/iframe/IFrameEmbedStrategy'
import { DefaultEmbedStyleControl } from '../iframe/default-embed-style-control'
import { FloatStrategyHandler } from '../embed-strategies/iframe/FloatStrategyHandler'
import { warn } from '../../utils/logger'
import { type EmbedIFrameComponent } from '../embed-component'

type EELayerOptions = Component.ComponentOptions & { component: HTMLElement }
function registerEELayerComponent () {
  const Component = vjs.getComponent('Component')
  class EELayer extends Component {
    component: HTMLElement
    constructor (player: VideoJsPlayer, options: EELayerOptions) {
      super(player, options)
      this.component = options.component
    }

    createEl (_tagName?: string, _properties?: any, _attributes?: any): Element {
      return (this.options_ as EELayerOptions).component
    }
  }
  vjs.registerComponent('EELayer', EELayer)
}
export class VideoJSTargetFactory implements EmbedTargetFactory<VideoJSEmbedTarget> {
  name: EmbedTargetType = 'video-js'
  supports (ctx: EmbedBeforeCreateElementContext): boolean {
    return (ctx.targetEl.classList.contains('video-js') && Object.prototype.hasOwnProperty.call(ctx.targetEl, 'player'))
  }

  create (ctx: EmbedElementContext): VideoJSEmbedTarget {
    return new VideoJSEmbedTarget(ctx)
  }

  async ready () {
    const vjs = window.videojs as unknown as VjsModule
    await waitUntil(() => vjs != null && vjs.getAllPlayers().length > 0)
  }

  detectFromDom (_option: DetectEmbedTargetsOptions): HTMLElement[] {
    const targetEls: HTMLElement[] = []
    // FUTURE: enhanced checks (ex: check javascript property etc)
    document.querySelectorAll('.video-js').forEach(el => {
      targetEls.push(el as HTMLElement)
    })
    return targetEls
  }
}

class VideoJSIFrameEmbedHandler implements IFrameEmbedHandler {
  iframeControl: DefaultEmbedStyleControl
  iFrameComponent: EmbedIFrameComponent

  constructor (public readonly embedTarget: VideoJSEmbedTarget) {}

  async initIFrame (iFrameComponent: EmbedIFrameComponent) {
    this.iframeControl = new DefaultEmbedStyleControl(this.embedTarget, iFrameComponent.rootEl)
    this.iFrameComponent = iFrameComponent
    if (vjs.getComponent('EELayer') == null) {
      registerEELayerComponent()
    }
    const playerEl = this.embedTarget.ctx.targetEl as (HTMLElement & { player: VideoJsPlayer })
    const vjsReadyPromise = new Promise<void>((resolve) => {
      playerEl.player.ready(() => { resolve() })
    })
    await vjsReadyPromise
    // Find the index of the component to insert ee layer after
    const componentAfter = this.embedTarget.ctx.settings.videojs?.afterComponent ?? 'PosterImage'
    const controlIdx = playerEl.player.children().findIndex(item => {
      return (typeof item.name === 'function') && item.name() === componentAfter
    })
    this.onTeleportUpdate().catch(warn)
    playerEl.player.addChild('EELayer', { component: this.iFrameComponent.rootEl } satisfies EELayerOptions, controlIdx)
  }

  get bottomSpace (): string {
    const defaultValue = this.embedTarget.ctx.settings.bottomSpace ?? '3em'
    const playerEl = this.embedTarget.ctx.targetEl as (HTMLElement & { player: VideoJsPlayer })
    const controlBarEl = playerEl?.player?.getChild('ControlBar')?.el()
    if (controlBarEl == null) return defaultValue
    return getComputedStyle(controlBarEl)?.height ?? defaultValue
  }

  async onTeleportUpdate () {
    this.iframeControl.updateStyle(
      this.embedTarget.ctx.teleportActive === true ? 'teleport' : 'overlay'
    )
  }

  destroy (): void { }

  show (): void {
    this.iFrameComponent.rootEl.style.setProperty('display', '')
  }

  hide (): void {
    this.iFrameComponent.rootEl.style.setProperty('display', 'none')
  }
}

type VjsModule = typeof window.videojs.default
const vjs = window.videojs as unknown as VjsModule

export class VideoJSEmbedTarget extends BaseEmbedTarget {
  mediaControl?: MediaControls
  constructor (ctx: EmbedElementContext) {
    super('video-js', ctx)
    this.ctx = ctx
    const mediaElement = this.ctx.targetEl
    this.mediaControl = new VideoJSMediaControls(mediaElement)
  }

  protected getIFrameEmbedHandler (): IFrameEmbedHandler {
    const strategy = this.ctx.settings.strategy
    if (strategy == null || strategy === 'parent') return new VideoJSIFrameEmbedHandler(this)
    else if (strategy === 'float-absolute' || strategy === 'float-fixed') return new FloatStrategyHandler(this)
    throw new Error('Invalid strategy `' + strategy + '` for HTMLMediaEmbedTarget')
  }

  get bottomSpace (): string {
    return this.ctx.settings.bottomSpace ?? '3em'
  }

  setPlaysInline (value: boolean): void {
    const playerEl = this.ctx.targetEl as (HTMLElement & { player: VideoJsPlayer })
    playerEl.player.playsinline(value)
  }
}
